import _ from 'lodash';
import * as packagePicker from '../packagePicker/packagePicker';
import * as dashboardAppService from '../services/dashboardAppService';
import * as superAppsGfppData from '../superApps/gfpp/superAppsGfppData';
import * as superApps from '../superApps/superApps';
import tpaConstants from '../constants/constants';
import * as bi from '../bi/bi';
import * as tpaUtils from '../utils/tpaUtils';
import * as settings from '../settings/settings';
import * as tpaHelpService from '../services/tpaHelpService';
import constants from '#packages/constants';
import * as helpIds from '#packages/helpIds';
import * as stateManagement from '#packages/stateManagement';
import * as util from '#packages/util';
import experiment from 'experiment';
import type { AppData, CompRef } from 'types/documentServices';
import type { EditorAPI } from '#packages/editorAPI';
import type { CompData } from '@wix/document-services-types';

const openSettingsPanel = function (editorAPI: EditorAPI, compRef: CompRef) {
  fireProvisionBIEvents(editorAPI, compRef);
  settings.open(editorAPI, compRef);
};

function fireProvisionBIEvents(editorAPI: EditorAPI, compRef: CompRef) {
  const selectedComponentData = editorAPI.components.data.get(compRef) || {};
  const appData = editorAPI.dsRead.tpa.app.getData(
    selectedComponentData.applicationId,
  );
  if (appData?.appDefinitionId) {
    fireAppIntent(editorAPI, bi.events.APP_INTENT, appData.appDefinitionId);
  }
}

function fireAppIntent(
  editorAPI: EditorAPI,
  biEvent: AnyFixMe,
  appDefId: string,
) {
  tpaUtils.bi.reportBIOnAppIntent(
    editorAPI,
    biEvent,
    appDefId,
    tpaConstants.BI.initiator.EDITOR,
    tpaConstants.BI.type.GFPP_SETTINGS,
  );
}

const defaultSettingsActionStructure = {
  label: 'gfpp_mainaction_tpa',
  automationId: 'gfpp-button-settings',
  isApplied: true,
  onClick: openSettingsPanel,
};

const openDashboardUrl = function (editorAPI: EditorAPI, url: string) {
  if (url) {
    window.open(url);
    sendOpenDashboardClickBi(editorAPI);
  }
};

const dashboardMainActionStructureWithData = function (
  editorAPI: EditorAPI,
  openInDashboardText: string,
) {
  return {
    label: openInDashboardText,
    isApplied: true,
    shouldTranslate: false,
    onClick() {
      dashboardAppService.getDashboardAppUrlAsync(
        editorAPI,
        undefined,
        // @ts-expect-error
        openDashboardUrl.bind(null, editorAPI),
      );
    },
  };
};

function addAppIdToActions(
  actions: (typeof defaultSettingsActionStructure)[],
  appData: AppData,
) {
  const appDefinitionId = appData?.appDefinitionId;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line you-dont-need-lodash-underscore/for-each
  _.forEach(actions, function (action) {
    // @ts-expect-error
    action.appId = appDefinitionId;
  });
}

function shouldShowSettingsInSecondaryLang(editorAPI: EditorAPI) {
  const compData = editorAPI.selection.getSelectedComponentData();
  if (!compData) {
    return false;
  }

  const appData = editorAPI.dsRead.tpa.app.getData(compData.applicationId);
  return superAppsGfppData.shouldShowSettingsInSecondaryLang(
    appData.appDefinitionId,
    compData.widgetId,
  );
}

function shouldShowTranslate(editorAPI: EditorAPI) {
  const compData = editorAPI.selection.getSelectedComponentData();
  if (!compData?.applicationId) {
    return false;
  }
  const appData = editorAPI.dsRead.tpa.app.getData(compData.applicationId);
  return superAppsGfppData.shouldShowTranslate(
    appData.appDefinitionId,
    compData.widgetId,
  );
}

function addSettingsActionIfNeeded(
  editorAPI: EditorAPI,
  actions: (typeof defaultSettingsActionStructure)[],
) {
  if (isSecondaryLang(editorAPI)) {
    if (shouldShowSettingsInSecondaryLang(editorAPI)) {
      actions.push(defaultSettingsActionStructure);
    }
  } else {
    actions.push(defaultSettingsActionStructure);
  }
}

const mainActions = function (editorAPI: EditorAPI, compRefs?: CompRef[]) {
  const [compRef] = util.array.asArray(compRefs || []);
  const actions: (typeof defaultSettingsActionStructure)[] = [];
  addSettingsActionIfNeeded(editorAPI, actions);
  addDashboardMainAction(editorAPI, actions);

  const selectedComponent =
    (compRef
      ? editorAPI.components.data.get(compRef)
      : editorAPI.selection.getSelectedComponentData()) || {};
  const appData = editorAPI.dsRead.tpa.app.getData(
    selectedComponent.applicationId,
  );

  if (appData && superAppsGfppData.hasCustomActions(appData.appDefinitionId)) {
    addCustomMainActions(
      editorAPI,
      appData.appDefinitionId,
      selectedComponent.widgetId,
      actions,
    );
  }

  addTranslationActionIfNeeded(editorAPI, actions);
  addAppIdToActions(actions, appData);

  return actions;
};

const mobileMainActions = function (
  editorAPI: EditorAPI,
  compRefs?: CompRef[],
) {
  const [compRef] = util.array.asArray(compRefs || []);
  const selectedComponentData =
    editorAPI.components.data.get(compRef) ||
    editorAPI.selection.getSelectedComponentData();
  const appData = editorAPI.dsRead.tpa.app.getData(
    selectedComponentData?.applicationId,
  );
  const isMobileSettingsEnabled =
    // @ts-expect-error
    appData?.widgets?.[selectedComponentData.widgetId]?.componentFields
      ?.mobileSettingsEnabled ?? false;

  return isMobileSettingsEnabled === true
    ? [defaultSettingsActionStructure]
    : [];
};

const enabledActions = function (
  editorAPI: EditorAPI,
  actions: typeof constants.ROOT_COMPS.GFPP.ACTIONS,
) {
  const result: (typeof constants.ROOT_COMPS.GFPP.ACTIONS)[keyof typeof constants.ROOT_COMPS.GFPP.ACTIONS][] =
    [actions.HELP];
  const compPointer = editorAPI.selection.getSelectedComponentData();
  if (compPointer && shouldShowUpgrade(editorAPI, compPointer)) {
    result.unshift(actions.UPGRADE);
  }
  return result;
};

const presetActions = function (editorAPI: EditorAPI) {
  const isAscendUpgrade = () => {
    const appData = getAppData(editorAPI);
    return appData && util.ascend.isAscendUpgrade(appData.appDefinitionId);
  };

  const actions = {
    help: {
      isSelected: false, // todo
      tooltip: 'gfpp_tooltip_help',
      onClick() {
        const appData = getAppData(editorAPI);
        const { widgetId } = editorAPI.selection.getSelectedComponentData();
        const biParams = {
          widget_id: widgetId,
          app_id: appData?.appDefinitionId,
          panel_name: editorAPI.selection.getSelectedComponentType(),
          origin: constants.BI.HELP.ORIGIN.GFPP,
        };
        const props = {
          openPanelBiEvent: bi.events.TPA_HELP_CLICK,
          openPanelBiParams: _.merge({ help_status: 'open' }, biParams),
          closePanelBiParams: _.merge({ help_status: 'close' }, biParams),
          closePanelBiEvent: bi.events.TPA_HELP_CLICK,
        };

        const getBiHelpParams = () => ({
          origin: constants.BI.HELP.ORIGIN.GFPP,
          panel_name: editorAPI.selection.getSelectedComponentType(),
          component: editorAPI.selection.getSelectedComponentType(),
          learn_more: false,
        });

        if (
          !stateManagement.multilingual.services.utils.currentIsOriginal(
            editorAPI,
          )
        ) {
          const biHelpParams = getBiHelpParams();
          editorAPI.panelManager.openHelpCenter(
            helpIds.MULTILINGUAL.GFPP,
            props,
            biHelpParams,
          );
          return;
        }
        tpaHelpService
          .getHelpId(
            editorAPI,
            appData?.appDefinitionId,
            widgetId,
            tpaHelpService.HELP_END_POINTS.WIDGET,
          )
          // @ts-expect-error
          .then(function (helpId: string) {
            const biHelpParams = getBiHelpParams();
            editorAPI.panelManager.openHelpCenter(helpId, props, biHelpParams);
          });
      },
    },
    upgrade: {
      isSelected: false, // todo
      shouldTranslate() {
        // @ts-expect-error
        return !dashboardAppService.getEditorGfppData(editorAPI).upgradeText;
      },
      tooltip() {
        return (
          // @ts-expect-error
          dashboardAppService.getEditorGfppData(editorAPI).upgradeText ||
          (isAscendUpgrade()
            ? 'gfpp_tooltip_ascend_upgrade'
            : 'tpa_package_picker_upgrade_title')
        );
      },
      icon: () => (isAscendUpgrade() ? 'ascend_upgrade_gfpp_icon' : 'upgrade'),
      onClick() {
        const appData = getAppData(editorAPI);
        tpaUtils.bi.reportBIOnAppIntent(
          editorAPI,
          bi.events.APP_INTENT,
          appData?.appDefinitionId,
          tpaConstants.BI.initiator.EDITOR,
          tpaConstants.BI.type.GFPP_UPGRADE,
        );
        packagePicker.startUpgradeFlow(
          editorAPI,
          appData?.appDefinitionId,
          false,
          {
            origin: 'gfpp',
          },
        );
        const params = {
          pp_origin: 'gfpp',
          app_site_id: appData?.instanceId,
          app_id: appData?.appDefinitionId,
        };
        editorAPI.bi.event(bi.events.CLICK_UPGRADE_APP, params);
      },
    },
  };

  // @ts-expect-error
  addAppIdToActions(actions, getAppData(editorAPI));
  return actions;
};

const sendOpenDashboardClickBi = function (editorAPI: EditorAPI) {
  const selectedComp = editorAPI.selection.getSelectedComponentData();
  const appData =
    selectedComp &&
    editorAPI.dsRead.tpa.app.getData(selectedComp.applicationId);
  const params = {
    app_id: appData?.appDefinitionId,
    app_site_id: appData?.instanceId,
    instance_id: selectedComp.id,
    origin: 'gfpp',
  };
  editorAPI.bi.event(bi.events.OPEN_DASHBOARD_APP_CLICK, params);
};

const shouldShowUpgrade = function (
  editorAPI: EditorAPI,
  selectedComponentData: CompData,
) {
  const appData = editorAPI.dsRead.tpa.app.getData(
    selectedComponentData.applicationId,
  );
  if (
    superAppsGfppData.hasExternalUpgrade(
      appData.appDefinitionId,
      selectedComponentData.widgetId,
    )
  ) {
    return true;
  }
  const canBeUpgraded = experiment.isOpen('se_appDefIdUpdates')
    ? editorAPI.dsRead.tpaV2.app.hasPremiumOffering(appData.appDefinitionId)
    : editorAPI.dsRead.tpa.app.hasPremiumOffering(
        selectedComponentData.applicationId,
      );

  if (canBeUpgraded) {
    const isFullyUpgraded = packagePicker.isFullyUpgraded(editorAPI, appData);
    return !isFullyUpgraded;
  }

  return false;
};

const getDashboardAction = function (editorAPI: EditorAPI) {
  let dashboardAction;
  const gfppMarketData = dashboardAppService.getEditorGfppData(editorAPI);
  // @ts-expect-error
  if (gfppMarketData.openInDashboard) {
    dashboardAction = dashboardMainActionStructureWithData(
      editorAPI,
      // @ts-expect-error
      gfppMarketData.openInDashboardText,
    );
  }
  return dashboardAction;
};

const addCustomMainActions = function (
  editorAPI: EditorAPI,
  appDefinitionId: string,
  widgetId: string,
  actions: (typeof defaultSettingsActionStructure)[],
) {
  const customData = superAppsGfppData.getCustomMainActionsFor(
    editorAPI,
    appDefinitionId,
    widgetId,
  );
  if (!_.isEmpty(customData)) {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/for-each
    _.forEach(customData.actions, function (action) {
      if (isSecondaryLang(editorAPI)) {
        return;
      }
      if (action.addAtBack) {
        addMainActionAtBack(action, actions);
      } else {
        addMainActionAtFront(action, actions);
      }
    });
  }
};

const getAppData = function (editorAPI: EditorAPI) {
  const selectedComponentData = editorAPI.selection.getSelectedComponentData();
  return (
    selectedComponentData &&
    editorAPI.dsRead.tpa.app.getData(selectedComponentData.applicationId)
  );
};

const addDashboardMainAction = function (
  editorAPI: EditorAPI,
  actions: (typeof defaultSettingsActionStructure)[],
) {
  const dashboardAction = getDashboardAction(editorAPI);
  if (!_.isEmpty(dashboardAction)) {
    actions.push(dashboardAction as AnyFixMe);
  }
};

function isSecondaryLang(editorAPI: EditorAPI) {
  const languageAPI = editorAPI.dsRead.language;
  return (
    languageAPI.multilingual.isEnabled() &&
    languageAPI.current.get() !== languageAPI.original.get()?.code
  );
}

const defaultTranslateActionStructure = {
  label: 'gfpp_mainaction_edit_translation',
  isApplied: true,
  onClick: (editorAPI: EditorAPI, compRef: CompRef) => {
    const selectedComponentData = editorAPI.components.data.get(compRef) || {};
    const appData =
      editorAPI.dsRead.tpa.app.getData(selectedComponentData.applicationId) ||
      ({} as AppData);
    const compData = editorAPI.selection.getSelectedComponentData();
    superApps.openTranslationDashboardUrl(
      appData.appDefinitionId,
      {
        onClose: _.partial(
          superApps.refreshAppCompsForAppDefId,
          appData.appDefinitionId,
        ),
        shouldNotCloseOnBlur: false,
        widgetId: selectedComponentData.widgetId,
        multilingualSchemaId: superAppsGfppData.getMultilingualSchemaId(
          appData.appDefinitionId,
          compData.widgetId,
        ),
      },
      { type: tpaConstants.BI.type.GFPP_MANAGE },
    );
  },
};

const defaultDisabledTranslateActionStructure = {
  label: 'gfpp_mainaction_edit_translation',
  isApplied: true,
  onClick: _.noop,
  isDisabled: true,
  tooltip: 'gfpp_tooltip_multilingual_translate_disabled',
};

const addTranslationActionIfNeeded = function (
  editorAPI: EditorAPI,
  actions: (typeof defaultSettingsActionStructure)[],
) {
  if (isSecondaryLang(editorAPI)) {
    if (shouldShowTranslate(editorAPI)) {
      addMainActionAtFront(
        defaultTranslateActionStructure as unknown as typeof defaultSettingsActionStructure,
        actions,
      );
    } else {
      addMainActionAtFront(
        defaultDisabledTranslateActionStructure as unknown as typeof defaultSettingsActionStructure,
        actions,
      );
    }
  }
};

const addMainActionAtFront = function (
  action: typeof defaultSettingsActionStructure,
  actions: (typeof defaultSettingsActionStructure)[],
) {
  if (!_.isEmpty(action)) {
    actions.unshift(action);
  }
};

const addMainActionAtBack = function (
  action: typeof defaultSettingsActionStructure,
  actions: (typeof defaultSettingsActionStructure)[],
) {
  if (!_.isEmpty(action)) {
    actions.push(action);
  }
};

export { mainActions, mobileMainActions, enabledActions, presetActions };
